<template>
  <Teleport to="body">
    <Transition name="compare-slide">
      <div
        v-if="compareModalOpen"
        class="compare-panel"
        :style="comparePanelStyle"
      >
        <DpplHeaderFooterContent class="compare-panel-body h-100 min-h-0 flex-grow-1">
          <template #header>
            <v-sheet
              class="top-bar d-flex"
              height="58px"
            >
              <div class="w-100 px-6 d-flex justify-space-between align-center">
                <span class="text-body-1">Compare Job Profiles</span>
                <v-btn
                  icon="mdi-close"
                  variant="text"
                  size="small"
                  aria-label="Close"
                  @click="closeCompareModal"
                />
              </div>
            </v-sheet>
          </template>

          <div
            v-if="loading"
            class="w-100 flex-grow-1 d-flex justify-center align-center compare-loading"
          >
            <v-progress-circular
              :size="40"
              :width="3"
              color="primary"
              indeterminate
            />
          </div>

          <template v-else>
            <ComparePane
              :profile="oldProfile"
              :fields="oldDiffFields"
              :subtitle="oldProfileSubtitle"
              :meta-html="isIdChangedScenario ? undefined : oldMetaDiffHtml"
              :is-current-version="oldVersionIsCurrentVersion"
              :is-diff-pane="!isIdChangedScenario"
              :title-html="isIdChangedScenario ? undefined : oldTitleDiffHtml"
              :acknowledged-at="compareSignedAt"
              :profile-ulid="isIdChangedScenario ? compareOldProfileUlid : null"
              right-border
              aria-label="Previously acknowledged job profile version"
            />
            <ComparePane
              :profile="newProfile"
              :fields="newDiffFields"
              :subtitle="newProfileSubtitle"
              :meta-html="isIdChangedScenario ? undefined : newMetaDiffHtml"
              :is-current-version="newVersionIsCurrentVersion"
              :profile-ulid="compareNewProfileUlid"
              :editor-open-version-history="!isIdChangedScenario"
              :is-diff-pane="!isIdChangedScenario"
              :title-html="isIdChangedScenario ? undefined : newTitleDiffHtml"
              aria-label="Current job profile"
            />
          </template>
        </DpplHeaderFooterContent>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  computed,
  onUnmounted,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import DpplHeaderFooterContent from '@ui/components/general/DpplHeaderFooterContent/DpplHeaderFooterContent.vue';
import { useAppBase } from '@/stores/app/base';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useEmployeeAcknowledgementsStore } from '@/stores/jobProfiles/employeeAcknowledgements';
import { useJobProfileCompare } from '@/composables/useJobProfileCompare';
import ComparePane from '@/components/library/employeeAcknowledgements/ComparePane.vue';

const { appHeaderHeight } = storeToRefs(useAppBase());
const { addErrorSnackbar } = useAppSnackbar();

const comparePanelStyle = computed(() => ({
  '--nav-offset': `${appHeaderHeight.value}px`,
}));

const {
  compareModalOpen,
  compareOldProfileUlid,
  compareOldVersionUlid,
  compareNewProfileUlid,
  compareNewVersionUlid,
  compareAlertType,
  compareSignedAt,
} = storeToRefs(useEmployeeAcknowledgementsStore());
const { closeCompareModal } = useEmployeeAcknowledgementsStore();

onUnmounted(() => {
  closeCompareModal();
});

const isIdChangedScenario = computed(() => (
  compareAlertType.value === 'job_profile_id_changed'
));

const oldSide = computed(() => ({
  isHrisProfile: true,
  profileUlid: compareOldProfileUlid.value,
  revisionUlid: compareOldVersionUlid.value,
}));

const newSide = computed(() => ({
  isHrisProfile: true,
  profileUlid: compareNewProfileUlid.value,
  revisionUlid: compareNewVersionUlid.value,
}));

const {
  loading,
  loadError,
  oldProfile,
  newProfile,
  oldDiffFields,
  newDiffFields,
  oldTitleDiffHtml,
  newTitleDiffHtml,
  oldMetaDiffHtml,
  newMetaDiffHtml,
  oldVersionIsCurrentVersion,
  newVersionIsCurrentVersion,
  oldProfileSubtitle,
  newProfileSubtitle,
} = useJobProfileCompare({
  active: compareModalOpen,
  oldSide,
  newSide,
});

watch(loadError, (newVal) => {
  if (newVal) {
    addErrorSnackbar({ text: newVal });
  }
}, { immediate: true });
</script>

<style scoped lang="less">
.compare-panel {
  position: fixed;
  top: var(--nav-offset);
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 7;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: @bg-default;
}

.compare-panel-body {
  min-height: 0;
}

.compare-slide-enter-active,
.compare-slide-leave-active {
  transition: transform 0.25s ease-out;
}

.compare-slide-enter-from,
.compare-slide-leave-to {
  transform: translateY(100%);
}

.top-bar {
  border-bottom: 1px solid @stroke-default;
}

.compare-loading {
  min-height: 0;
}
</style>
